import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';

import UsersService from 'api/users/UsersService';
import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { OrderData, ListingTableColumn, FilterData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import Logger from 'common/services/Logger';
import { Reducers } from 'store/types';
import Toast from 'common/services/Toast';
import Button from 'common/components/button/Button';

import { SupplierDto } from 'api/suppliers/models/SupplierDto';
import { SupplierSearchCriteria } from 'api/suppliers/models/SupplierSearchCriteria';
import SuppliersService from 'api/suppliers/SuppliersService';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import CountriesService from 'api/countries/CountriesService';
import { CountryDto } from 'api/countries/models/CountryDto';

const PAGE_ID = 'LIBRARY_SUPPLIERS_LIST_V2';
const baseUrl = '/library/suppliers';

function SuppliersListScreen (): JSX.Element | null {
    const { t } = useTranslation();
    const [tableData, setTableData] = useState<ListingTableData<SupplierDto, Partial<SupplierSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'name', orderColumn: 's.name', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null
    }));
    const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [supplierToDelete, setSupplierToDelete] = useState<SupplierDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['SUPPLIERS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['SUPPLIERS_READ']);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [countries, setCountries] = useState<CountryDto[]>([]);

    const countriesOptions = useMemo<SelectInputOption[]>(() =>
        countries
            .map(x => ({
                label: t(`countries.${x.isoCodeSmall}`, { defaultValue: x.name }),
                value: x.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    [countries, t]);

    const countryIsoById = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const c of countries) {
            map[c.id] = c.isoCodeSmall;
        }
        return map;
    }, [countries]);

    const translateCountry = (countryId?: string | null, fallback?: string | null) => {
        if (!countryId) return fallback ?? '';
        const iso = countryIsoById[countryId];
        if (!iso) return fallback ?? '';
        return t(`countries.${iso}`, { defaultValue: fallback ?? '' });
    };

    const columns: ListingTableColumn<SupplierDto>[] = orderColumns([
        {
            name: t('suppliers.list.name'),
            field: 'name',
            filter: { type: 'text', value: tableData.filters.name ?? '', placeholder: t('suppliers.list.name') },
            minWidth: '18rem',
            orderColumn: 's.name'
        },
        {
            name: t('suppliers.list.nif'),
            field: 'nif',
            filter: { type: 'text', value: tableData.filters.nif ?? '', placeholder: t('suppliers.list.nif') },
            minWidth: '12rem',
            orderColumn: 's.nif'
        },
        {
            name: t('suppliers.list.email'),
            field: 'email',
            filter: { type: 'text', value: tableData.filters.email ?? '', placeholder: t('suppliers.list.email') },
            minWidth: '18rem',
            orderColumn: 's.email'
        },
        {
            name: t('suppliers.list.phone'),
            field: 'phone',
            filter: { type: 'text', value: tableData.filters.phone ?? '', placeholder: t('suppliers.list.phone') },
            minWidth: '14rem',
            orderColumn: 's.phone'
        },
        {
            name: t('suppliers.list.city'),
            field: 'city',
            filter: { type: 'text', value: tableData.filters.city ?? '', placeholder: t('suppliers.list.city') },
            minWidth: '14rem',
            orderColumn: 's.city'
        },
        {
            name: t('suppliers.list.country'),
            field: 'countryId',
            filter: { type: 'dropdown', value: tableData.filters.countryId ?? '', options: countriesOptions },
            minWidth: '14rem',
            orderColumn: 'c.name',
            renderCell: (row) => <div>{translateCountry(row.countryId, row.countryName)}</div>
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDate?.start ? new Date(tableData.filters.updatedDate.start) : null,
                    end: tableData.filters.updatedDate?.end ? new Date(tableData.filters.updatedDate.end) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name'
        }
    ], tableData.columnsOrder);

    const renderAction = (row: SupplierDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: `${baseUrl}/edit/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) }
            ]}
        />
    );

    const onFilter = (col: ListingTableColumn<SupplierDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({
                ...d,
                page: 1
            });
            if (col.field === 'name') {
                data.filters = {
                    ...data.filters,
                    name: filterData.value as string
                };
            }
            if (col.field === 'nif') {
                data.filters = {
                    ...data.filters,
                    nif: filterData.value as string
                };
            }
            if (col.field === 'email') {
                data.filters = {
                    ...data.filters,
                    email: filterData.value as string
                };
            }
            if (col.field === 'phone') {
                data.filters = {
                    ...data.filters,
                    phone: filterData.value as string
                };
            }
            if (col.field === 'city') {
                data.filters = {
                    ...data.filters,
                    city: filterData.value as string
                };
            }
            if (col.field === 'countryId') {
                data.filters = {
                    ...data.filters,
                    countryId: filterData.value as string
                };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDate: filterData.value,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }
            if (col.field === 'userUpdatedName') {
                data.filters = {
                    ...data.filters,
                    userUpdatedName: filterData.value as string
                };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    };

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    };

    const onOrder = (orderData: OrderData<SupplierDto>) => {
        setRefreshKey(k => k + 1);
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    };

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        try {
            if (isLoading) {
                return;
            }

            Loading.show();
            setIsLoading(true);

            const data = await SuppliersService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = data.currentPage === 1 ? data.items : [...suppliers, ...data.items];
            setHasMore(data.totalItems > newListElements.length);
            setSuppliers(newListElements);
            setTotalItems(data.totalItems);

            if (firstLoad.current) {
                const countriesData = await CountriesService.getCatalog();
                setCountries(countriesData);

                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get suppliers list', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setIsLoading(false);
        }
    };

    const onRowClick = (row: SupplierDto) => {
        ListingTableCache.saveLastRowId(PAGE_ID, row.id);
    };

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    };

    const onOpenModalToDelete = (row: SupplierDto) => {
        setSupplierToDelete(row);
        setShowConfirmDeleteModal(true);
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }
        try {
            await SuppliersService.remove(supplierToDelete as SupplierDto);

            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);

            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete supplier', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false);
    };

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('suppliers.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('suppliers.title'), url: '/library/suppliers' }
                ]}
            />
            <PageHeader title={t('suppliers.title')} subTitle={`${totalItems} ${t('common.total_results')}`} addSidebarSpacing informationText={t('common.list')} >
                {canWrite && <Button href={`${baseUrl}/create`}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<SupplierDto>
                    columns={columns}
                    rows={suppliers}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={onRowClick}
                    onHref={(row) => `${baseUrl}/details/${row.id}`}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={supplierToDelete?.name}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default SuppliersListScreen;
